import { useRef } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

/**
 * Route order for directional slide transitions.
 * Lower index = further left. Navigating from lower → higher slides from right.
 */
const ROUTE_ORDER = ['/albums', '/events', '/admin'];

function getRouteIndex(pathname: string): number {
  for (let i = 0; i < ROUTE_ORDER.length; i++) {
    if (pathname.startsWith(ROUTE_ORDER[i])) return i;
  }
  return -1;
}

function getRouteGroup(pathname: string): string {
  const match = pathname.match(/^\/[^/]*/);
  return match ? match[0] : pathname;
}

const slideVariants = {
  initial: (dir: number) => ({
    x: dir === 0 ? 0 : dir > 0 ? '50%' : '-50%',
    opacity: 0,
  }),
  animate: {
    x: 0,
    opacity: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 30, mass: 0.9 },
  },
  exit: (dir: number) => ({
    x: dir === 0 ? 0 : dir > 0 ? '-25%' : '25%',
    opacity: 0,
    transition: { duration: 0.15, ease: 'easeIn' as const },
  }),
};

/**
 * Drop-in replacement for <Outlet /> with directional slide transitions
 * between top-level nav routes (Albums ↔ Events ↔ Admin).
 * Sub-route navigation within a group (e.g. /albums → /albums/:id) is not animated.
 */
export function DirectionalOutlet() {
  const location = useLocation();
  const currentOutlet = useOutlet();
  const prevIndexRef = useRef(getRouteIndex(location.pathname));
  const directionRef = useRef(0);

  const currentIndex = getRouteIndex(location.pathname);
  // eslint-disable-next-line react-hooks/refs -- intentional "previous value" pattern; refs must be read during render to compute direction synchronously
  if (currentIndex !== prevIndexRef.current) {
    if (currentIndex >= 0 && prevIndexRef.current >= 0) { // eslint-disable-line react-hooks/refs
      directionRef.current = currentIndex > prevIndexRef.current ? 1 : -1; // eslint-disable-line react-hooks/refs
    } else {
      directionRef.current = 0; // eslint-disable-line react-hooks/refs
    }
    prevIndexRef.current = currentIndex; // eslint-disable-line react-hooks/refs
  }

  const routeGroup = getRouteGroup(location.pathname);
  const direction = directionRef.current; // eslint-disable-line react-hooks/refs

  return (
    <div style={{ overflowX: 'hidden' }}>
      <AnimatePresence mode="wait" custom={direction} initial={false}>
        <motion.div
          key={routeGroup}
          custom={direction}
          variants={slideVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          {currentOutlet}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
